import { useEffect, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { eventsAPI, tracksAPI, galleriesAPI } from '../../lib/api';
import api from '../../lib/api';
import { trackClick } from '../../lib/analytics';
import { 
  MapPin, Calendar, Clock, Users, ArrowLeft, ArrowRight, Rocket, ExternalLink, 
  Car, Train, Plane, Bus, Navigation, Ticket, Quote, 
  ChevronRight, Star, Building2, Mic2, Tag, Layers, Image, ChevronLeft
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useSEO } from '../../hooks/useSEO';
import { useIsMobile } from '../../hooks/useIsMobile';
import { RssFeedSlider } from '../../components/public/RssFeedSlider';
import { ChallengesTeaserSection } from '../../components/public/ChallengesTeaserSection';
import { getSectionAnchor } from '../../utils/anchor';
import ShareButton from '../../components/common/ShareButton';

// Helper function to format date as dd-mm-YYYY
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

// Format date in Italian long format
const formatDateLong = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('it-IT', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
};

// Icon mapping for directions
const directionIcons = {
  car: Car,
  train: Train,
  plane: Plane,
  bus: Bus,
  default: Navigation
};

// Icon mapping for stats
const statIcons = {
  users: Users,
  speakers: Mic2,
  companies: Building2,
  default: Star
};

// Markdown rendering imported from shared util
import { renderMarkdownText } from '../../utils/markdown';

export const EventDetailPage = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const previewToken = searchParams.get('preview');
  const isMobile = useIsMobile();
  const [event, setEvent] = useState(null);
  const [agenda, setAgenda] = useState(null);
  const [speakers, setSpeakers] = useState([]);
  const [galleries, setGalleries] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [lightboxImage, setLightboxImage] = useState(null);

  // SEO with event-specific keywords, image and description (include venue and city in title)
  const locationPart = event ? (
    event.venue_name && event.city 
      ? `${event.venue_name} - ${event.city}`
      : event.venue_name || event.city || ''
  ) : '';
  const eventTitle = event ? (locationPart ? `${event.title} - ${locationPart}` : event.title) : '';
  useSEO(
    eventTitle, 
    event?.seo_keywords || [],
    event?.hero_image || '',
    event?.description?.substring(0, 160) || ''
  );

  // Countdown effect
  useEffect(() => {
    if (!event?.date) return;
    
    const calculateCountdown = () => {
      const eventDate = new Date(event.date);
      eventDate.setHours(9, 0, 0, 0); // Assume event starts at 9:00
      const now = new Date();
      const diff = eventDate - now;
      
      if (diff <= 0) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      setCountdown({ days, hours, minutes, seconds });
    };
    
    calculateCountdown();
    const interval = setInterval(calculateCountdown, 1000);
    return () => clearInterval(interval);
  }, [event?.date]);

  useEffect(() => {
    loadData();
  }, [slug, previewToken]);

  const loadData = async () => {
    try {
      const eventRes = await eventsAPI.getOne(slug, previewToken);
      setEvent(eventRes.data);

      const agendaRes = await eventsAPI.getAgenda(slug);
      setAgenda(agendaRes.data);

      const speakersRes = await eventsAPI.getSpeakers(slug);
      setSpeakers(speakersRes.data);

      // Load galleries for this event
      if (eventRes.data?.id) {
        try {
          const galleriesRes = await galleriesAPI.getByEvent(eventRes.data.id);
          setGalleries(galleriesRes.data);
        } catch (e) {
          console.log('No galleries for this event');
        }
        // Load challenges linked to this event (public only, exclude child rounds)
        try {
          const chRes = await api.get('/challenges', { params: { event_id: eventRes.data.id, public_only: true } });
          const list = (chRes.data || []).filter(c => !c.parent_challenge_id);
          const withJudges = await Promise.all(list.map(async (ch) => {
            try {
              const jRes = await api.get(`/challenges/${ch.id}/judges?only_visible=true`);
              return { ...ch, judges: jRes.data || [] };
            } catch { return { ...ch, judges: [] }; }
          }));
          setChallenges(withJudges);
        } catch (e) { /* no challenges */ }
      }
    } catch (error) {
      console.error('Error loading event:', error);
    } finally {
      setLoading(false);
      // Scroll to hash target once content is rendered.
      // Browser native hash-scroll fires too early on SPAs (target not in DOM yet).
      if (window.location.hash) {
        const target = window.location.hash.slice(1);
        // Defer until next paint cycles to let React commit + images load layout.
        const attempt = (tries = 10) => {
          const el = document.getElementById(target);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          } else if (tries > 0) {
            setTimeout(() => attempt(tries - 1), 150);
          }
        };
        // Wait a tick so React renders content_sections
        requestAnimationFrame(() => requestAnimationFrame(() => attempt()));
      }
    }
  };

  const getFullUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };

  const getYoutubeEmbedUrl = (url) => {
    if (!url) return null;
    // Handle various YouTube URL formats
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 
      ? `https://www.youtube.com/embed/${match[2]}?autoplay=1&mute=1&controls=1&rel=0&modestbranding=1` 
      : url;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse-glow w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
          <Rocket className="text-primary animate-bounce" size={32} />
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6">
        <h1 className="text-2xl font-heading font-bold">Evento non trovato</h1>
        <Link to="/editions">
          <Button variant="outline">
            <ArrowLeft className="mr-2" size={16} />
            Torna alle Edizioni
          </Button>
        </Link>
      </div>
    );
  }

  const heroUrl = getFullUrl(event.hero_image);
  const heroMobileUrl = event.hero_image_mobile ? getFullUrl(event.hero_image_mobile) : null;
  const activeHeroUrl = isMobile && heroMobileUrl ? heroMobileUrl : heroUrl;

  return (
    <div className="relative" data-testid="event-detail-page">
      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center" data-testid="hero-section">
        {heroUrl && (
          <>
            <img
              src={isMobile && heroMobileUrl ? heroMobileUrl : heroUrl}
              alt={event.title}
              className="absolute inset-0 w-full h-full object-cover"
              data-testid="event-hero-bg"
            />
            {/* Desktop gradient overlays */}
            <div className="absolute inset-0 bg-gradient-to-r from-sios-void via-sios-void/90 to-sios-void/60" data-testid="hero-overlay-desktop-1" />
            <div className="absolute inset-0 bg-gradient-to-t from-sios-void via-transparent to-sios-void/40" data-testid="hero-overlay-desktop-2" />
            {/* Mobile dark veil (darker gradient for better readability) */}
            <div className="absolute inset-0 bg-gradient-to-b from-sios-void/85 via-sios-void/80 to-sios-void/95" data-testid="hero-overlay-mobile" />
          </>
        )}

        <div className="max-w-7xl mx-auto w-full px-6 py-24 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            {/* Left Column - Content */}
            <div className="max-w-2xl">
            {/* Back Link */}
            <Link 
              to="/editions" 
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-8"
            >
              <ArrowLeft size={16} />
              Tutte le Edizioni
            </Link>

            {/* Header Banner (if enabled) */}
            {event.header_banner_enabled && (event.header_banner_text || event.header_banner_image) && (
              <div className="mb-6 p-4 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10" data-testid="header-banner">
                {event.header_banner_text && (
                  <p className="text-sm sm:text-base text-white/80 mb-3">
                    {event.header_banner_text}
                  </p>
                )}
                {event.header_banner_image && (
                  <img 
                    src={event.header_banner_image.startsWith('http') 
                      ? event.header_banner_image 
                      : `${process.env.REACT_APP_BACKEND_URL}${event.header_banner_image}`
                    }
                    alt="Banner"
                    className="max-h-16 w-auto object-contain"
                  />
                )}
              </div>
            )}

            {/* Event Title */}
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-6">
              {event.title}
            </h1>

            {/* Date & Location */}
            <div className="flex flex-wrap gap-4 mb-6">
              {event.date && (
                <div className="flex items-center gap-2 text-lg">
                  <Calendar className="text-primary" size={24} />
                  <div>
                    <span className="capitalize">{formatDateLong(event.date)}</span>
                    {event.end_date && event.end_date !== event.date && (
                      <span> - {formatDateLong(event.end_date)}</span>
                    )}
                    {event.event_start_time && (
                      <span className="ml-2 text-primary">
                        (a partire dalle {event.event_start_time})
                      </span>
                    )}
                  </div>
                </div>
              )}
              <button 
                onClick={() => {
                  trackClick(event.id, 'location', null, event.venue_name || event.city);
                  const section = document.getElementById('come-raggiungerci');
                  if (section) section.scrollIntoView({ behavior: 'smooth' });
                }}
                className="flex items-center gap-2 text-lg hover:text-secondary transition-colors cursor-pointer group"
              >
                <MapPin className="text-secondary group-hover:scale-110 transition-transform" size={24} />
                <span className="font-semibold underline-offset-4 group-hover:underline">
                  {event.venue_name && event.city 
                    ? `${event.venue_name} - ${event.city}`
                    : event.venue_name || event.city || event.location}
                </span>
              </button>
            </div>

            {/* Countdown - Below date/time/location (hidden when fully zero) */}
            {event.date && (countdown.days + countdown.hours + countdown.minutes + countdown.seconds) > 0 && (
              <div className="flex items-center gap-4 mb-8 flex-wrap">
                <p className="text-sm text-muted-foreground">L'evento inizia tra:</p>
                <div className="flex gap-3">
                  <div className="text-center">
                    <div className="bg-primary/20 rounded-lg px-3 py-2">
                      <p className="font-heading text-2xl font-bold text-primary">{countdown.days}</p>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Giorni</p>
                  </div>
                  <div className="text-center">
                    <div className="bg-primary/20 rounded-lg px-3 py-2">
                      <p className="font-heading text-2xl font-bold text-primary">{String(countdown.hours).padStart(2, '0')}</p>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Ore</p>
                  </div>
                  <div className="text-center">
                    <div className="bg-primary/20 rounded-lg px-3 py-2">
                      <p className="font-heading text-2xl font-bold text-primary">{String(countdown.minutes).padStart(2, '0')}</p>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Min</p>
                  </div>
                  <div className="text-center">
                    <div className="bg-primary/20 rounded-lg px-3 py-2">
                      <p className="font-heading text-2xl font-bold text-primary">{String(countdown.seconds).padStart(2, '0')}</p>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Sec</p>
                  </div>
                </div>
              </div>
            )}

            {/* Description */}
            {event.description && (
              <p className="text-lg text-muted-foreground leading-relaxed mb-8 max-w-2xl">
                {event.description}
              </p>
            )}

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4 items-center">
              {event.ticket_url && (
                <a 
                  href={event.ticket_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  onClick={() => trackClick(event.id, 'ticket', null, 'Ottieni il tuo Pass')}
                >
                  <Button 
                    size="lg" 
                    className="rounded-full bg-primary hover:bg-primary/90 neon-shadow-strong px-8"
                    data-testid="ticket-cta"
                  >
                    <Ticket className="mr-2" size={20} />
                    {event.ticket_label || 'Ottieni il tuo Pass'}
                  </Button>
                </a>
              )}
              {/* Share Button */}
              <ShareButton type="event" slug={event.slug || slug} title={event.title} />
            </div>
            </div>

            {/* Right Column - Video (desktop only) */}
            {event.hero_video && (
              <div className="hidden lg:flex items-center justify-center">
                <div className="w-[85%]">
                  <div className="glass rounded-2xl overflow-hidden border border-primary/30 shadow-2xl">
                    <div className="aspect-video">
                      <iframe
                        src={getYoutubeEmbedUrl(event.hero_video)}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        title="Event Video"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Mobile Video Section */}
      {event.hero_video && (
        <section className="lg:hidden py-6 px-6 bg-black/30">
          <div className="max-w-2xl mx-auto">
            <div className="rounded-2xl overflow-hidden border border-primary/30">
              <div className="aspect-video">
                <iframe
                  src={getYoutubeEmbedUrl(event.hero_video)}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title="Event Video"
                />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Stats Bar */}
      {event.stats && event.stats.length > 0 && (
        <section className="py-12 bg-primary/5 border-y border-primary/20" data-testid="stats-section">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {event.stats.map((stat, index) => {
                const IconComponent = statIcons[stat.icon] || statIcons.default;
                return (
                  <div key={index} className="text-center">
                    <div className="flex justify-center mb-3">
                      <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                        <IconComponent className="text-primary" size={24} />
                      </div>
                    </div>
                    <p className="font-heading text-3xl md:text-4xl font-bold text-primary">{stat.value}</p>
                    <p className="text-sm text-muted-foreground mt-1">{stat.label}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Topics/Tags */}
      {event.topics && event.topics.length > 0 && (
        <section className="py-12 border-b border-white/10" data-testid="topics-section">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-center gap-3 mb-6">
              <Tag className="text-primary" size={20} />
              <h2 className="font-heading text-xl font-semibold">Topics</h2>
            </div>
            <div className="flex flex-wrap gap-3">
              {event.topics.map((topic, index) => (
                <span 
                  key={index}
                  className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm font-medium hover:border-primary/50 hover:bg-primary/10 transition-colors cursor-default"
                >
                  {topic}
                </span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Custom Content Sections (New Format) */}
      {event.content_sections && event.content_sections.length > 0 && (
        <>
          {[...event.content_sections]
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map((section) => (
              <section
                key={section.id}
                id={getSectionAnchor(section)}
                className="py-16 border-b border-white/10 scroll-mt-24"
                data-testid={`content-section-${section.id}`}
              >
                <div className="max-w-7xl mx-auto px-6">
                  {section.title && (
                    <div className="flex items-center gap-3 mb-10">
                      <Layers className="text-primary" size={24} />
                      <h2 className="font-heading text-2xl md:text-3xl font-bold">{section.title}</h2>
                    </div>
                  )}
                  
                  {/* Background container for the entire section content */}
                  <div className="rounded-2xl overflow-hidden bg-white/5 border border-white/10 p-6 md:p-8">
                    <div className="space-y-8">
                      {(section.blocks || [])
                        .sort((a, b) => (a.order || 0) - (b.order || 0))
                        .map((block) => {
                          const columns = block.columns || 1;
                          const gridCols = columns === 1 ? 'grid-cols-1' : columns === 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';
                          
                          return (
                            <div key={block.id} className={`grid ${gridCols} gap-8`}>
                              {(block.items || []).map((item, itemIdx) => {
                                // Check if item has any content
                                const hasContent = 
                                  (item.type === 'text' && item.text) ||
                                  (item.type === 'image' && item.image) ||
                                  (item.type === 'cta' && item.cta_text) ||
                                  (item.type === 'text_cta' && (item.text || item.cta_text));
                                
                                if (!hasContent) return null;

                                return (
                                  <div key={item.id || itemIdx}>
                                    {/* Text */}
                                    {item.type === 'text' && item.text && (
                                      <div 
                                        className={`prose prose-invert max-w-none ${
                                          item.fontSize === 'sm' ? 'text-sm' :
                                          item.fontSize === 'lg' ? 'text-lg' :
                                          item.fontSize === 'xl' ? 'text-xl' :
                                          item.fontSize === '2xl' ? 'text-2xl' : ''
                                        }`}
                                        style={{
                                          textAlign: item.textAlign || 'left'
                                        }}
                                      >
                                        {renderMarkdownText(item.text)}
                                      </div>
                                    )}
                                    
                                    {/* Image */}
                                    {item.type === 'image' && item.image && (
                                      <div style={{ textAlign: 'center' }}>
                                        <img 
                                          src={item.image.startsWith('http') ? item.image : `${process.env.REACT_APP_BACKEND_URL}${item.image}`}
                                          alt=""
                                          className="img-contain rounded-lg inline-block"
                                          style={{
                                            width: item.imageHeight && item.imageHeight !== 'auto' && item.imageHeight !== '100' 
                                              ? `${item.imageHeight}%` 
                                              : '100%'
                                          }}
                                        />
                                      </div>
                                    )}
                                    
                                    {/* CTA */}
                                    {item.type === 'cta' && item.cta_text && (
                                      <div className="flex items-start">
                                        <a 
                                          href={item.cta_link || '#'}
                                          target={item.cta_link?.startsWith('http') ? '_blank' : '_self'}
                                          rel="noopener noreferrer"
                                          className="inline-flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground font-medium rounded-full transition-colors"
                                        >
                                          {item.cta_text}
                                          <ArrowRight size={16} />
                                        </a>
                                      </div>
                                    )}

                                    {/* Text + CTA */}
                                    {item.type === 'text_cta' && (
                                      <div className="space-y-4">
                                        {item.text && (
                                          <div 
                                            className={`prose prose-invert max-w-none ${
                                              item.fontSize === 'sm' ? 'text-sm' :
                                              item.fontSize === 'lg' ? 'text-lg' :
                                              item.fontSize === 'xl' ? 'text-xl' : ''
                                            }`}
                                          >
                                            {renderMarkdownText(item.text)}
                                          </div>
                                        )}
                                        {item.cta_text && (
                                          <div className="pt-2 flex justify-end">
                                            <a 
                                              href={item.cta_link || '#'}
                                              target={item.cta_link?.startsWith('http') ? '_blank' : '_self'}
                                              rel="noopener noreferrer"
                                              className="inline-flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground font-medium rounded-full transition-colors"
                                            >
                                              {item.cta_text}
                                              <ArrowRight size={16} />
                                            </a>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
              </section>
            ))}
        </>
      )}

      {/* Legacy Content Blocks Section (Backwards Compatibility) */}
      {(!event.content_sections || event.content_sections.length === 0) && event.content_blocks && event.content_blocks.length > 0 && (
        <section className="py-16 border-b border-white/10" data-testid="content-blocks-section">
          <div className="max-w-7xl mx-auto px-6">
            {event.content_section_title && (
              <div className="flex items-center gap-3 mb-10">
                <Layers className="text-primary" size={24} />
                <h2 className="font-heading text-2xl md:text-3xl font-bold">{event.content_section_title}</h2>
              </div>
            )}
            
            {/* Render blocks */}
            <div className="space-y-8">
              {[...event.content_blocks]
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map((block) => {
                  // Support new format with columns and items
                  if (block.items && Array.isArray(block.items)) {
                    const columns = block.columns || 1;
                    const gridCols = columns === 1 ? 'grid-cols-1' : columns === 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';
                    
                    return (
                      <div key={block.id} className={`grid ${gridCols} gap-6`}>
                        {block.items.map((item) => (
                          <div 
                            key={item.id} 
                            className="rounded-2xl overflow-hidden bg-white/5 border border-white/10 p-6"
                          >
                            {/* Text */}
                            {item.type === 'text' && item.text && (
                              <div className="prose prose-invert max-w-none">
                                {renderMarkdownText(item.text)}
                              </div>
                            )}
                            
                            {/* Image */}
                            {item.type === 'image' && item.image && (
                              <img 
                                src={item.image.startsWith('http') ? item.image : `${process.env.REACT_APP_BACKEND_URL}${item.image}`}
                                alt=""
                                className="w-full h-auto rounded-lg object-cover"
                              />
                            )}
                            
                            {/* CTA */}
                            {item.type === 'cta' && item.cta_text && (
                              <div className="flex items-center justify-center h-full">
                                <a 
                                  href={item.cta_link || '#'}
                                  target={item.cta_link?.startsWith('http') ? '_blank' : '_self'}
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground font-medium rounded-full transition-colors"
                                >
                                  {item.cta_text}
                                  <ArrowRight size={16} />
                                </a>
                              </div>
                            )}

                            {/* Text + CTA */}
                            {item.type === 'text_cta' && (
                              <div className="space-y-4">
                                {item.text && (
                                  <div className="prose prose-invert max-w-none">
                                    {renderMarkdownText(item.text)}
                                  </div>
                                )}
                                {item.cta_text && (
                                  <div className="pt-4 flex justify-end">
                                    <a 
                                      href={item.cta_link || '#'}
                                      target={item.cta_link?.startsWith('http') ? '_blank' : '_self'}
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground font-medium rounded-full transition-colors"
                                    >
                                      {item.cta_text}
                                      <ArrowRight size={16} />
                                    </a>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  }
                  
                  // Fallback for old format (backwards compatibility)
                  const gridCols = (block.row_size || 1) === 1 ? 'grid-cols-1' : (block.row_size || 1) === 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';
                  
                  return (
                    <div key={block.id} className={`grid ${gridCols} gap-8`}>
                      <div className="rounded-2xl overflow-hidden bg-white/5 border border-white/10">
                        {/* Image (if present) */}
                        {(block.type === 'image' || block.type === 'mixed') && block.image && (
                          <div 
                            className="relative overflow-hidden flex justify-center"
                            style={{ padding: block.type === 'image' ? '1rem' : 0 }}
                          >
                            <img 
                              src={block.image.startsWith('http') ? block.image : `${process.env.REACT_APP_BACKEND_URL}${block.image}`}
                              alt=""
                              className="object-cover rounded-lg"
                              style={{ 
                                width: `${block.imageSize || 100}%`,
                                maxWidth: '100%'
                              }}
                            />
                          </div>
                        )}
                        
                        {/* Text (if present) */}
                        {(block.type === 'text' || block.type === 'mixed') && block.text && (
                          <div 
                            className="p-6"
                            style={{
                              fontSize: `${block.typography?.fontSize || 16}px`,
                              fontWeight: block.typography?.fontWeight || 'normal',
                              color: block.typography?.color || '#ffffff',
                              textAlign: block.typography?.textAlign || 'left',
                              lineHeight: block.typography?.lineHeight || 1.6
                            }}
                          >
                            {block.text.split('\n').map((paragraph, pIdx) => (
                              <p key={pIdx} className={pIdx > 0 ? 'mt-4' : ''}>
                                {paragraph}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </section>
      )}

      {/* Speakers Section */}
      {speakers.length > 0 && (
        <section className="py-20" data-testid="speakers-section">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <p className="text-xs font-mono uppercase tracking-[0.3em] text-primary mb-3">Relatori</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold">I Nostri Speaker</h2>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
              {speakers.map((speaker) => (
                <Link 
                  key={speaker.id}
                  to={`/speakers/${speaker.slug}`}
                  className="group"
                  data-testid={`speaker-card-${speaker.id}`}
                  onClick={() => trackClick(event.id, 'speaker', speaker.id, `${speaker.first_name} ${speaker.last_name}`)}
                >
                  <div className="text-center">
                    <div className="relative mb-4 mx-auto w-32 h-32 md:w-40 md:h-40">
                      <img 
                        src={getFullUrl(speaker.image) || `https://ui-avatars.com/api/?name=${speaker.first_name}+${speaker.last_name}&background=7B2CBF&color=fff&size=160`}
                        alt={`${speaker.first_name} ${speaker.last_name}`}
                        className="w-full h-full rounded-2xl object-cover ring-2 ring-white/10 group-hover:ring-primary/50 transition-all"
                      />
                    </div>
                    <h3 className="font-heading font-semibold group-hover:text-primary transition-colors">
                      {speaker.first_name} {speaker.last_name}
                    </h3>
                    {(speaker.title || speaker.company) && (
                      <p className="text-sm text-muted-foreground line-clamp-2">{speaker.title && speaker.company ? `${speaker.title} - ${speaker.company}` : (speaker.title || speaker.company)}</p>
                    )}
                  </div>
                </Link>
              ))}
            </div>

            <div className="text-center mt-10">
              <Link to="/speakers">
                <Button variant="outline" className="rounded-full px-8">
                  Vedi tutti gli Speaker
                  <ChevronRight size={18} className="ml-1" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Challenges Teaser Section */}
      <ChallengesTeaserSection challenges={challenges} />

      {/* Agenda Section */}
      {agenda && agenda.tracks && agenda.tracks.length > 0 && (
        <section className="py-20 bg-black/20" data-testid="agenda-section">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <p className="text-xs font-mono uppercase tracking-[0.3em] text-primary mb-3">Programma</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold">Agenda</h2>
            </div>

            <Tabs defaultValue={agenda.tracks[0]?.id} className="w-full">
              {/* Mobile: Horizontal scroll, Desktop: Centered wrap */}
              <div className="relative mb-10">
                <div className="md:hidden overflow-x-auto scrollbar-hide -mx-6 px-6 pt-2">
                  <TabsList className="inline-flex gap-3 bg-transparent min-w-max pb-2">
                    {agenda.tracks.map((track) => (
                      <TabsTrigger 
                        key={track.id}
                        value={track.id}
                        className="px-5 py-2.5 rounded-full border border-white/10 whitespace-nowrap text-sm data-[state=active]:bg-primary data-[state=active]:border-primary data-[state=active]:text-white flex-shrink-0"
                        style={{ minWidth: 'calc((100vw - 48px - 24px) / 2.5)' }}
                        onClick={() => trackClick(event.id, 'agenda_tab', track.id, track.name)}
                      >
                        {track.name}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </div>
                <TabsList className="hidden md:flex flex-wrap justify-center gap-2 bg-transparent">
                  {agenda.tracks.map((track) => (
                    <TabsTrigger 
                      key={track.id}
                      value={track.id}
                      className="px-6 py-3 rounded-full border border-white/10 data-[state=active]:bg-primary data-[state=active]:border-primary data-[state=active]:text-white"
                      onClick={() => trackClick(event.id, 'agenda_tab', track.id, track.name)}
                    >
                      {track.name}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>

              {agenda.tracks.map((track) => {
                const trackSessions = agenda.sessions?.filter(s => s.track_id === track.id) || [];
                return (
                  <TabsContent key={track.id} value={track.id}>
                    {trackSessions.length > 0 ? (
                      <div className="flex flex-col gap-6">
                        {trackSessions.map((session) => (
                          <article 
                            key={session.id} 
                            className="glass rounded-2xl p-8 hover:border-primary/30 transition-all cursor-pointer"
                            data-testid={`session-${session.id}`}
                            onClick={() => trackClick(event.id, 'agenda', session.id, session.title)}
                          >
                            <div className="flex flex-col md:flex-row md:items-start gap-6">
                              <div className="flex items-center gap-2 text-base font-mono text-primary shrink-0 bg-primary/10 px-4 py-2 rounded-lg">
                                <Clock size={18} />
                                {session.start_time} - {session.end_time}
                              </div>

                              <div className="flex-1">
                                <h4 className="font-heading font-semibold text-xl mb-3">
                                  {session.title}
                                </h4>
                                {session.description && (
                                  <p className="text-muted-foreground mb-6 leading-relaxed">
                                    {session.description}
                                  </p>
                                )}

                                {session.speakers?.length > 0 && (
                                  <div className="flex flex-wrap gap-4">
                                    {session.speakers.map((speaker) => (
                                      <Link 
                                        key={speaker.id}
                                        to={`/speakers/${speaker.slug}`}
                                        className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors group"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          trackClick(event.id, 'speaker', speaker.id, `${speaker.first_name} ${speaker.last_name}`);
                                        }}
                                      >
                                        <img 
                                          src={speaker.image || `https://ui-avatars.com/api/?name=${speaker.first_name}+${speaker.last_name}&background=7B2CBF&color=fff&size=80`}
                                          alt={`${speaker.first_name} ${speaker.last_name}`}
                                          className="w-12 h-12 rounded-full object-cover ring-2 ring-primary/30 group-hover:ring-primary transition-all"
                                        />
                                        <div>
                                          <p className="font-medium group-hover:text-primary transition-colors">
                                            {speaker.first_name} {speaker.last_name}
                                          </p>
                                          {(speaker.title || speaker.company) && (
                                            <p className="text-xs text-muted-foreground">{speaker.title && speaker.company ? `${speaker.title} - ${speaker.company}` : (speaker.title || speaker.company)}</p>
                                          )}
                                        </div>
                                      </Link>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </article>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-16 glass rounded-2xl">
                        <Clock className="mx-auto text-muted-foreground/50 mb-4" size={40} />
                        <p className="text-muted-foreground">Nessuna sessione per questo track</p>
                      </div>
                    )}
                  </TabsContent>
                );
              })}
            </Tabs>
          </div>
        </section>
      )}

      {/* Gallery Section - Horizontal Slider */}
      {galleries && galleries.length > 0 && (
        <section className="py-20 bg-black/20" data-testid="gallery-section">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <p className="text-xs font-mono uppercase tracking-[0.3em] text-primary mb-3">Galleria</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold">Immagini dell'Evento</h2>
            </div>

            {galleries.map((gallery) => (
              <div key={gallery.id} className="mb-12 last:mb-0">
                {/* Gallery Title - Clickable to gallery page */}
                <Link 
                  to={`/gallery/${gallery.slug}`}
                  className="inline-flex items-center gap-2 text-xl font-heading font-bold mb-4 hover:text-primary transition-colors"
                  onClick={() => trackClick(event.id, 'gallery', gallery.id, gallery.title)}
                >
                  <Image size={20} className="text-primary" />
                  {gallery.title}
                  <ChevronRight size={16} />
                </Link>

                {/* Horizontal scrolling gallery with carousel */}
                <div className="relative group/carousel">
                  {/* Left Arrow */}
                  <button
                    type="button"
                    onClick={() => {
                      const container = document.getElementById(`gallery-scroll-${gallery.id}`);
                      if (container) container.scrollBy({ left: -320, behavior: 'smooth' });
                    }}
                    className="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-black/70 hover:bg-black/90 flex items-center justify-center text-white transition-all opacity-0 group-hover/carousel:opacity-100 shadow-lg"
                    aria-label="Scorri a sinistra"
                  >
                    <ChevronLeft size={24} />
                  </button>
                  
                  {/* Right Arrow */}
                  <button
                    type="button"
                    onClick={() => {
                      const container = document.getElementById(`gallery-scroll-${gallery.id}`);
                      if (container) container.scrollBy({ left: 320, behavior: 'smooth' });
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-black/70 hover:bg-black/90 flex items-center justify-center text-white transition-all opacity-0 group-hover/carousel:opacity-100 shadow-lg"
                    aria-label="Scorri a destra"
                  >
                    <ChevronRight size={24} />
                  </button>

                  <div 
                    id={`gallery-scroll-${gallery.id}`}
                    className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scroll-smooth"
                    style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
                  >
                    {gallery.images?.map((image, index) => (
                      <div 
                        key={`${gallery.id}-img-${index}`}
                        className="flex-shrink-0 snap-start cursor-pointer group"
                        onClick={() => {
                          trackClick(event.id, 'gallery_image', `${gallery.id}_${index}`, image.caption || `Immagine ${index + 1}`);
                          setLightboxImage(getFullUrl(image.url));
                        }}
                      >
                        <div className="relative w-72 h-48 md:w-80 md:h-52 rounded-xl overflow-hidden bg-white/5">
                          <img
                            src={getFullUrl(image.url)}
                            alt={image.caption || `Gallery image ${index + 1}`}
                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105 !opacity-100"
                          />
                          {/* Caption overlay on hover */}
                          {image.caption && (
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3 pointer-events-none">
                              <p className="text-white text-sm font-medium line-clamp-2">{image.caption}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}

                    {/* View all link at end */}
                    <Link 
                      to={`/gallery/${gallery.slug}`}
                      className="flex-shrink-0 snap-start w-48 h-48 md:w-52 md:h-52 rounded-xl bg-primary/20 hover:bg-primary/30 transition-colors flex flex-col items-center justify-center gap-2 text-primary"
                    >
                      <Image size={32} />
                      <span className="font-medium">Vedi tutto</span>
                      <span className="text-sm text-primary/70">{gallery.images?.length || 0} foto</span>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Image Lightbox */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button 
            className="absolute top-4 right-4 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors z-10"
            onClick={() => setLightboxImage(null)}
          >
            <span className="text-2xl">×</span>
          </button>
          <img
            src={lightboxImage}
            alt="Gallery"
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Testimonials Section */}
      {event.testimonials && event.testimonials.length > 0 && (
        <section className="py-20" data-testid="testimonials-section">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <p className="text-xs font-mono uppercase tracking-[0.3em] text-primary mb-3">Cosa dicono</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold">Testimonials</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {event.testimonials.map((testimonial, index) => (
                <div 
                  key={index}
                  className="glass rounded-2xl p-6 flex flex-col"
                >
                  <Quote className="text-primary/50 mb-4" size={32} />
                  <p className="text-lg italic mb-6 flex-1">"{testimonial.quote}"</p>
                  <div className="flex items-center gap-4">
                    {testimonial.image ? (
                      <img 
                        src={getFullUrl(testimonial.image)}
                        alt={testimonial.author}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                        <span className="text-primary font-bold">{testimonial.author?.charAt(0)}</span>
                      </div>
                    )}
                    <div>
                      <p className="font-semibold">{testimonial.author}</p>
                      <p className="text-sm text-muted-foreground">
                        {testimonial.role}{testimonial.company && `, ${testimonial.company}`}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* RSS Feed Section */}
      {event.rss_feed_url && (
        <RssFeedSlider 
          feedUrl={event.rss_feed_url} 
          title={event.rss_feed_title || "News & Articoli"} 
        />
      )}

      {/* Sponsors Section - Grouped */}
      {(() => {
        // Prefer new nested sponsor_groups; fallback to legacy flat sponsors grouped by tier
        let groups = [];
        if (Array.isArray(event.sponsor_groups) && event.sponsor_groups.length > 0) {
          groups = event.sponsor_groups
            .map((g) => ({ name: g.name || 'Partner', items: g.items || [] }))
            .filter((g) => g.items.length > 0);
        } else if (Array.isArray(event.sponsors) && event.sponsors.length > 0) {
          const byTier = {};
          const order = [];
          event.sponsors.forEach((s) => {
            const t = s.tier || 'Partner';
            if (!byTier[t]) { byTier[t] = []; order.push(t); }
            byTier[t].push(s);
          });
          groups = order.map((t) => ({ name: t, items: byTier[t] }));
        }
        if (groups.length === 0) return null;
        return (
          <section className="py-20 bg-black/20" data-testid="sponsors-section">
            <div className="max-w-7xl mx-auto px-6">
              {groups.map((group, tierIndex) => (
                <div key={`${group.name}-${tierIndex}`} className={tierIndex > 0 ? 'mt-16' : ''}>
                  {/* Tier Header */}
                  <div className="text-center mb-8">
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-white/90">{group.name}</h2>
                  </div>

                  {/* Sponsors Grid: 2 columns mobile, 4 columns desktop */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                    {group.items.map((sponsor, index) => (
                      <a
                        key={`${group.name}-${index}`}
                        href={sponsor.url || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-center group"
                        data-testid={`sponsor-${group.name}-${index}`}
                        onClick={() => trackClick(event.id, 'sponsor', `${group.name}_${index}`, sponsor.name)}
                      >
                        {/* Logo Box */}
                        <div
                          className="flex items-center justify-center p-8 rounded-2xl border border-gray-200 transition-all min-h-[120px] mb-3"
                          style={{ backgroundColor: '#ffffff' }}
                        >
                          {sponsor.logo ? (
                            <img
                              src={getFullUrl(sponsor.logo)}
                              alt={sponsor.name}
                              className="w-full h-full object-contain max-h-16"
                            />
                          ) : (
                            <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                              <span className="font-heading text-2xl font-bold text-primary">
                                {sponsor.name?.charAt(0)}
                              </span>
                            </div>
                          )}
                        </div>
                        {/* Name below */}
                        <p className="font-heading font-semibold text-sm group-hover:text-primary transition-colors">
                          {sponsor.name}
                        </p>
                      </a>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        );
      })()}

      {/* Gallery Section */}
      {event.gallery && event.gallery.length > 0 && (
        <section className="py-20" data-testid="gallery-section">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <p className="text-xs font-mono uppercase tracking-[0.3em] text-primary mb-3">Galleria</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold">Immagini dell'Evento</h2>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {event.gallery.map((image, index) => (
                <div 
                  key={index}
                  className="aspect-square rounded-xl overflow-hidden group cursor-pointer"
                >
                  <img 
                    src={getFullUrl(image)}
                    alt={`Gallery ${index + 1}`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Come Raggiungerci Section */}
      {(event.google_maps_embed || event.directions?.length > 0 || event.address) && (
        <section id="come-raggiungerci" className="py-20 bg-black/20" data-testid="directions-section">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-center gap-4 mb-10">
              <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                <Navigation className="text-primary" size={24} />
              </div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold">
                Come Raggiungerci
              </h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Google Maps */}
              <div className="flex flex-col gap-6">
                {event.google_maps_embed ? (
                  <div className="aspect-video rounded-2xl overflow-hidden">
                    <iframe
                      src={event.google_maps_embed}
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Mappa"
                      className="w-full h-full"
                    />
                  </div>
                ) : event.address ? (
                  <div className="aspect-video rounded-2xl overflow-hidden">
                    <iframe
                      src={`https://www.google.com/maps?q=${encodeURIComponent(event.address)}&output=embed`}
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Mappa"
                      className="w-full h-full"
                    />
                  </div>
                ) : null}

                {(event.venue_name || event.location || event.address) && (
                  <div className="glass rounded-xl p-5 flex items-start gap-4">
                    <MapPin className="text-primary flex-shrink-0 mt-1" size={24} />
                    <div className="space-y-1">
                      {(event.venue_name || event.location) && (
                        <>
                          <p className="text-sm text-muted-foreground">Struttura</p>
                          <p className="font-heading font-semibold text-lg">{event.venue_name || event.location}</p>
                        </>
                      )}
                      {event.address && (
                        <>
                          <p className="text-sm text-muted-foreground mt-3">Indirizzo</p>
                          <p className="font-medium">{event.address}</p>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Directions */}
              {event.directions?.length > 0 && (
                <div className="flex flex-col gap-6">
                  {event.directions.map((direction, index) => {
                    const IconComponent = directionIcons[direction.icon] || directionIcons.default;
                    return (
                      <div 
                        key={index}
                        className="glass rounded-xl p-6 flex items-start gap-4"
                      >
                        <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                          <IconComponent className="text-primary" size={20} />
                        </div>
                        <div>
                          <h4 className="font-heading font-semibold mb-1">{direction.title}</h4>
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {direction.description}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Floating Ticket CTA (mobile) with countdown — hidden when countdown is fully zero */}
      {event.ticket_url && (countdown.days + countdown.hours + countdown.minutes + countdown.seconds) > 0 && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 p-4 bg-sios-void/95 backdrop-blur-lg border-t border-white/10 z-50">
          <div className="flex items-center justify-between gap-4">
            <div className="flex gap-2 text-center">
              <div className="bg-primary/20 rounded-lg px-2 py-1">
                <p className="font-heading text-lg font-bold text-primary">{countdown.days}</p>
                <p className="text-[10px] text-muted-foreground">GG</p>
              </div>
              <div className="bg-primary/20 rounded-lg px-2 py-1">
                <p className="font-heading text-lg font-bold text-primary">{String(countdown.hours).padStart(2, '0')}</p>
                <p className="text-[10px] text-muted-foreground">ORE</p>
              </div>
              <div className="bg-primary/20 rounded-lg px-2 py-1">
                <p className="font-heading text-lg font-bold text-primary">{String(countdown.minutes).padStart(2, '0')}</p>
                <p className="text-[10px] text-muted-foreground">MIN</p>
              </div>
            </div>
            <a href={event.ticket_url} target="_blank" rel="noopener noreferrer" className="flex-1 max-w-xs">
              <Button className="w-full rounded-full bg-primary hover:bg-primary/90">
                <Ticket className="mr-2" size={16} />
                Ottieni il tuo Pass
              </Button>
            </a>
          </div>
        </div>
      )}
    </div>
  );
};
